import React, { useEffect, useMemo, useState } from "react";
import { Alert, Form, Modal } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";

function formatDate(value) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString();
}

function downloadBlob(blob, fileName) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName || "document";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
}

function getFileIconClass(fileName, fileType) {
  const normalizedName = String(fileName || "").toLowerCase();
  const normalizedType = String(fileType || "").toLowerCase();

  if (
    normalizedType.startsWith("image/") ||
    normalizedName.endsWith(".png") ||
    normalizedName.endsWith(".jpg") ||
    normalizedName.endsWith(".jpeg") ||
    normalizedName.endsWith(".gif") ||
    normalizedName.endsWith(".bmp") ||
    normalizedName.endsWith(".webp") ||
    normalizedName.endsWith(".svg")
  ) {
    return "bi-file-earmark-image text-info";
  }
  if (normalizedName.endsWith(".pdf") || normalizedType.includes("pdf")) {
    return "bi-file-earmark-pdf text-danger";
  }
  if (normalizedName.endsWith(".doc") || normalizedName.endsWith(".docx") || normalizedType.includes("word")) {
    return "bi-file-earmark-word text-primary";
  }
  if (normalizedName.endsWith(".xls") || normalizedName.endsWith(".xlsx") || normalizedType.includes("excel")) {
    return "bi-file-earmark-excel text-success";
  }
  if (normalizedName.endsWith(".ppt") || normalizedName.endsWith(".pptx") || normalizedType.includes("presentation")) {
    return "bi-file-earmark-ppt text-warning";
  }
  if (
    normalizedName.endsWith(".zip") ||
    normalizedName.endsWith(".rar") ||
    normalizedName.endsWith(".7z") ||
    normalizedName.endsWith(".tar") ||
    normalizedName.endsWith(".gz") ||
    normalizedType.includes("zip") ||
    normalizedType.includes("compressed")
  ) {
    return "bi-file-earmark-zip text-secondary";
  }
  if (normalizedType.startsWith("video/")) {
    return "bi-file-earmark-play text-warning";
  }
  if (normalizedType.startsWith("audio/")) {
    return "bi-file-earmark-music text-success";
  }

  return "bi-file-earmark text-muted";
}

function inferPreviewKind(fileName, fileType) {
  const normalizedName = String(fileName || "").toLowerCase();
  const normalizedType = String(fileType || "").toLowerCase();

  if (normalizedType.startsWith("image/")) {
    return "image";
  }
  if (normalizedType.startsWith("video/")) {
    return "video";
  }
  if (normalizedType.startsWith("audio/")) {
    return "audio";
  }
  if (normalizedType.includes("pdf") || normalizedName.endsWith(".pdf")) {
    return "pdf";
  }
  if (
    normalizedType.startsWith("text/") ||
    normalizedName.endsWith(".txt") ||
    normalizedName.endsWith(".csv") ||
    normalizedName.endsWith(".json") ||
    normalizedName.endsWith(".xml") ||
    normalizedName.endsWith(".md") ||
    normalizedName.endsWith(".log")
  ) {
    return "text";
  }

  return "unsupported";
}

function getUploadErrorMessage(err, fallbackMessage) {
  if (err.response?.status === 413) {
    return "This file is too large for the server upload limit. Please upload a smaller file or ask the administrator to increase the upload limit.";
  }

  return err.response?.data?.error || fallbackMessage;
}

function DocumentFolderDetailPage() {
  const history = useHistory();
  const { uuid } = useParams();
  const [folder, setFolder] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingFolderUuid, setDeletingFolderUuid] = useState("");
  const [deletingFileUuid, setDeletingFileUuid] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    saving: false
  });
  const [previewState, setPreviewState] = useState({
    open: false,
    loading: false,
    file: null,
    previewUrl: "",
    previewText: "",
    previewKind: ""
  });

  useEffect(() => {
    fetchPageData();
  }, [uuid]);

  useEffect(() => {
    return () => {
      if (previewState.previewUrl) {
        window.URL.revokeObjectURL(previewState.previewUrl);
      }
    };
  }, [previewState.previewUrl]);

  const createdByName = useMemo(() => {
    const user = users.find((item) => String(item.id) === String(folder?.created_by_id || ""));
    return user ? [user.firstname, user.lastname].filter(Boolean).join(" ") || user.username : "-";
  }, [folder, users]);

  const totalItems = Number(folder?.child_folder_count || 0) + Number(folder?.file_count || 0);

  async function fetchPageData() {
    setLoading(true);
    setError("");

    try {
      const [folderResponse, usersResponse] = await Promise.all([
        api.get(`/document-folders/${uuid}`),
        api.get("/users")
      ]);

      setFolder(folderResponse.data);
      setUsers(usersResponse.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load document folder details.");
    } finally {
      setLoading(false);
    }
  }

  async function handleChildFolderDelete(childFolderUuid) {
    setDeletingFolderUuid(childFolderUuid);

    try {
      await api.delete(`/document-folders/${childFolderUuid}`);
      toast.success("Folder deleted");
      await fetchPageData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete folder.");
    } finally {
      setDeletingFolderUuid("");
    }
  }

  function openUploadModal() {
    setUploadForm({
      file: null,
      saving: false
    });
    setShowUploadModal(true);
  }

  function closeUploadModal() {
    setShowUploadModal(false);
    setUploadForm({
      file: null,
      saving: false
    });
  }

  async function handleUpload() {
    if (!uploadForm.file) {
      toast.error("Please choose a file to upload.");
      return;
    }

    setUploadForm((current) => ({
      ...current,
      saving: true
    }));

    const formData = new FormData();
    formData.append("file", uploadForm.file);

    try {
      await api.post(`/document-folders/${uuid}/files`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Document uploaded");
      await fetchPageData();
      closeUploadModal();
    } catch (err) {
      toast.error(getUploadErrorMessage(err, "Failed to upload document."));
      setUploadForm((current) => ({
        ...current,
        saving: false
      }));
    }
  }

  async function handleFileDownload(file) {
    try {
      const response = await api.get(`/document-folders/${uuid}/files/${file.uuid}`, {
        responseType: "blob"
      });
      downloadBlob(response.data, file.file_name);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to download document.");
    }
  }

  async function handleFilePreview(file) {
    setPreviewState((current) => {
      if (current.previewUrl) {
        window.URL.revokeObjectURL(current.previewUrl);
      }

      return {
        open: true,
        loading: true,
        file,
        previewUrl: "",
        previewText: "",
        previewKind: inferPreviewKind(file.file_name, file.file_type)
      };
    });

    try {
      const response = await api.get(`/document-folders/${uuid}/files/${file.uuid}`, {
        responseType: "blob"
      });

      const resolvedKind = inferPreviewKind(file.file_name, response.data?.type || file.file_type);
      const previewUrl = resolvedKind === "unsupported" ? "" : window.URL.createObjectURL(response.data);
      const previewText = resolvedKind === "text" ? await response.data.text() : "";

      setPreviewState({
        open: true,
        loading: false,
        file,
        previewUrl,
        previewText,
        previewKind: resolvedKind
      });
    } catch (err) {
      setPreviewState({
        open: false,
        loading: false,
        file: null,
        previewUrl: "",
        previewText: "",
        previewKind: ""
      });
      toast.error(err.response?.data?.error || "Failed to preview document.");
    }
  }

  function closePreviewModal() {
    setPreviewState((current) => {
      if (current.previewUrl) {
        window.URL.revokeObjectURL(current.previewUrl);
      }

      return {
        open: false,
        loading: false,
        file: null,
        previewUrl: "",
        previewText: "",
        previewKind: ""
      };
    });
  }

  async function handleFileDelete(fileUuid) {
    setDeletingFileUuid(fileUuid);

    try {
      await api.delete(`/document-folders/${uuid}/files/${fileUuid}`);
      toast.success("Document deleted");
      await fetchPageData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete document.");
    } finally {
      setDeletingFileUuid("");
    }
  }

  return (
    <div>
        <PageHeader
        breadcrumbs={
          folder?.breadcrumbs?.length
            ? [
                { label: "Documents Management", to: "/documents" },
                ...folder.breadcrumbs.map((crumb, index) => ({
                  label: crumb.name,
                  to: index === folder.breadcrumbs.length - 1 ? null : `/documents/${crumb.uuid}`
                }))
              ]
            : [{ label: "Documents Management", to: "/documents" }]
        }
        title="Documents Management"
        subtitle={folder ? folder.name : "Loading document folder details..."}
      />

      {loading ? <LoadingState label="Loading document folder details..." /> : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}

      {!loading && folder ? (
        <div className="content-card document-browser-shell">
            <div className="document-browser-toolbar">
              <div className="document-browser-toolbar-left">
              <div className="document-browser-mini-meta">
                <span>{totalItems} item{totalItems === 1 ? "" : "s"}</span>
                <span>Created by {createdByName}</span>
                <span>Updated {formatDate(folder.last_updated)}</span>
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button type="button" className="document-explorer-action-button" onClick={() => history.push(`/documents/new?parent_uuid=${uuid}`)}>
                <span className="document-explorer-action-icon">
                  <i className="bi bi-folder-plus" />
                </span>
                <span>New folder</span>
              </button>
              <button type="button" className="document-explorer-action-button" onClick={openUploadModal}>
                <span className="document-explorer-action-icon">
                  <i className="bi bi-file-earmark-plus" />
                </span>
                <span>New file</span>
              </button>
            </div>
          </div>

          {!folder.child_folders?.length && !folder.files?.length ? (
            <div className="document-browser-empty">This folder is empty. Add a subfolder or upload any file.</div>
          ) : null}

          {folder.child_folders?.length ? (
            <div className="document-browser-group">
              <div className="document-browser-group-title">Folders</div>
              <div className="table-responsive document-explorer-table-wrap">
                <table className="table align-middle mb-0 document-explorer-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th className="document-explorer-date-cell">Last modified</th>
                      <th className="text-end document-explorer-action-cell">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {folder.child_folders.map((childFolder) => (
                      <tr key={childFolder.uuid}>
                        <td className="document-explorer-name-cell">
                          <button
                            type="button"
                            className="document-explorer-name-button"
                            onClick={() => history.push(`/documents/${childFolder.uuid}`)}
                          >
                            <span className="document-explorer-folder-icon">
                              <i className="bi bi-folder-fill" />
                            </span>
                            <span className="document-explorer-name-copy">{childFolder.name || "-"}</span>
                          </button>
                        </td>
                        <td className="document-explorer-date-cell">{formatDate(childFolder.last_updated)}</td>
                        <td className="text-end document-explorer-action-cell">
                          <div className="d-inline-flex gap-2 justify-content-end flex-nowrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => history.push(`/documents/${childFolder.uuid}/edit`)}
                            >
                              <i className="bi bi-pencil-square me-2" />
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleChildFolderDelete(childFolder.uuid)}
                              disabled={deletingFolderUuid === childFolder.uuid}
                            >
                              {deletingFolderUuid === childFolder.uuid ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-trash me-2" />
                                  Delete
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {folder.files?.length ? (
            <div className="document-browser-group">
              <div className="document-browser-group-title">Documents</div>
              <div className="table-responsive document-explorer-table-wrap">
                <table className="table align-middle mb-0 document-explorer-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th className="document-explorer-date-cell">Last modified</th>
                      <th className="text-end document-explorer-action-cell">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {folder.files.map((file) => (
                      <tr key={file.uuid}>
                        <td className="document-explorer-name-cell">
                          <button type="button" className="document-explorer-name-button" onClick={() => handleFilePreview(file)}>
                            <span className="document-explorer-file-icon">
                              <i className={`bi ${getFileIconClass(file.file_name, file.file_type)}`}></i>
                            </span>
                            <span className="document-explorer-name-copy">{file.file_name || "-"}</span>
                          </button>
                        </td>
                        <td className="document-explorer-date-cell">{formatDate(file.last_updated || file.created_at)}</td>
                        <td className="text-end document-explorer-action-cell">
                          <div className="d-inline-flex gap-2 justify-content-end flex-nowrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleFilePreview(file)}
                            >
                              <i className="bi bi-eye me-2" />
                              View
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleFileDownload(file)}
                            >
                              <i className="bi bi-download me-2" />
                              Download
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleFileDelete(file.uuid)}
                              disabled={deletingFileUuid === file.uuid}
                            >
                              {deletingFileUuid === file.uuid ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-trash me-2" />
                                  Delete
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <Modal show={showUploadModal} onHide={closeUploadModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label">Choose file</label>
          <Form.Control
            type="file"
            onChange={(event) =>
              setUploadForm((current) => ({
                ...current,
                file: event.target.files?.[0] || null
              }))
            }
          />
          <div className="form-text mt-2">All file types are allowed in document management.</div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={closeUploadModal}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleUpload} disabled={uploadForm.saving}>
            {uploadForm.saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Uploading...
              </>
            ) : (
              "Upload document"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={previewState.open} onHide={closePreviewModal} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{previewState.file?.file_name || "Document preview"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewState.loading ? <LoadingState label="Loading preview..." /> : null}

          {!previewState.loading && previewState.previewKind === "image" ? (
            <div className="text-center">
              <img
                src={previewState.previewUrl}
                alt={previewState.file?.file_name || "Document preview"}
                style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
              />
            </div>
          ) : null}

          {!previewState.loading && previewState.previewKind === "pdf" ? (
            <iframe
              title={previewState.file?.file_name || "Document preview"}
              src={previewState.previewUrl}
              style={{ width: "100%", height: "70vh", border: 0 }}
            />
          ) : null}

          {!previewState.loading && previewState.previewKind === "video" ? (
            <video src={previewState.previewUrl} controls style={{ width: "100%", maxHeight: "70vh" }} />
          ) : null}

          {!previewState.loading && previewState.previewKind === "audio" ? (
            <audio src={previewState.previewUrl} controls style={{ width: "100%" }} />
          ) : null}

          {!previewState.loading && previewState.previewKind === "text" ? (
            <pre
              style={{
                maxHeight: "70vh",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                marginBottom: 0
              }}
            >
              {previewState.previewText || "This file is empty."}
            </pre>
          ) : null}

          {!previewState.loading && previewState.previewKind === "unsupported" ? (
            <Alert variant="light" className="mb-0 document-preview-unsupported-alert">
              This file type cannot be previewed directly in the browser. Use the download button to open it in the appropriate application.
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={closePreviewModal}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => (previewState.file ? handleFileDownload(previewState.file) : null)}
            disabled={!previewState.file}
          >
            Download
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default DocumentFolderDetailPage;
